import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { CLIENT_CONTACT_FIELD_LABELS, getMissingClientContactFields } from '../../constants/clientConstants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UPPERCASE_FIELDS = new Set(['address', 'address_number', 'city']);

// Avviso "anagrafica incompleta": mostrato in Dimissioni.js/Ospitalita.js/CanoneRai.js/
// EstrattoInps.js/Imu.js subito
// dopo che un documento caricato abbina un cliente GIÀ esistente (per CF) la cui scheda manca
// però di dati di contatto di base (indirizzo, cellulare, email...) — permette di completarli
// con un click, senza uscire dalla pratica che si sta creando per andare in Anagrafica Clienti.
const ClientCompletionBanner = ({ client, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  const missing = getMissingClientContactFields(client);

  useEffect(() => {
    if (open) {
      const initial = {};
      missing.forEach((k) => { initial[k] = ''; });
      setValues(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!client || missing.length === 0) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = { ...client };
      missing.forEach((k) => {
        const v = (values[k] || '').trim();
        updated[k] = UPPERCASE_FIELDS.has(k) ? v.toUpperCase() : v;
      });
      const res = await axios.put(`${API}/clients/${client.id}`, updated);
      toast.success('Anagrafica cliente completata');
      setOpen(false);
      if (onSaved) onSaved(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio anagrafica');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="flex items-start gap-2 border border-amber-300 bg-amber-50 text-amber-800 rounded-lg p-3 text-sm">
        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          Cliente abbinato, ma l'anagrafica è incompleta — manca: {missing.map((k) => CLIENT_CONTACT_FIELD_LABELS[k]).join(', ')}.
        </div>
        <Button type="button" size="sm" variant="outline" className="border-amber-400 text-amber-800 hover:bg-amber-100 flex-shrink-0" onClick={() => setOpen(true)}>
          Completa ora
        </Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Completa anagrafica cliente</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {missing.map((k) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-xs text-slate-500">{CLIENT_CONTACT_FIELD_LABELS[k]}</Label>
                <Input
                  type={k === 'email' ? 'email' : 'text'}
                  value={values[k] || ''}
                  onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button type="button" onClick={handleSave} disabled={saving}>
              {saving ? 'Salvataggio...' : 'Salva'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClientCompletionBanner;
